import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { hasAnyPermission } from '@platform/auth/permissions';
import type { RbacRole } from '@shared/types/rbac';
import { format, formatDistanceToNow } from 'date-fns';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAssignPersonRoleMutation,
  useBustRbacCacheMutation,
  useGetAdminPersonRolesQuery,
  useGetAdminRolesQuery,
  useUnassignPersonRoleMutation,
} from '../../store/api/apiSlice';
import type { RootState } from '../../store/store';
import type { CoachMemberItem } from './api/coachesApi';
import {
  useGetCoachQuery,
  useUpdateCoachSettingsMutation,
  useUpdateCoachStatusMutation,
} from './api/coachesApi';
import { useStartImpersonationMutation } from './api/impersonationApi';
import CoachProfileNavigation from './components/CoachProfileNavigation';
import DismissableViewsPanel from './components/DismissableViewsPanel';
import { startAdminImpersonationSession } from './utils/adminImpersonation';

type MemberSortBy = 'name' | 'status' | 'sessions' | 'artifacts' | 'summaries' | 'last_activity';
type SortDir = 'asc' | 'desc';

const GOOGLE_PROVIDER_LABELS: Record<string, string> = {
  'google-drive': 'Drive',
  'google-calendar': 'Calendar',
  'google-gmail': 'Gmail',
};

const formatGoogleProviderLabel = (provider: string) =>
  GOOGLE_PROVIDER_LABELS[(provider || '').trim().toLowerCase()] || provider;

const getLastActivity = (member: CoachMemberItem): Date | null => {
  const artifactDate = member.last_artifact_at ? new Date(member.last_artifact_at) : null;
  const sessionDate = member.last_session_at ? new Date(member.last_session_at) : null;
  if (artifactDate && sessionDate) {
    return artifactDate > sessionDate ? artifactDate : sessionDate;
  }
  return artifactDate || sessionDate;
};

const formatLastActivity = (member: CoachMemberItem): string => {
  const date = getLastActivity(member);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : '-';
};

export default function CoachDetailPage() {
  const { coachId } = useParams<{ coachId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((s: RootState) => s.auth.user);
  const currentToken = useSelector((s: RootState) => s.auth.token);
  const permissions = currentUser?.permissions ?? [];
  const userRoles = currentUser?.roles ?? [];
  const canManageUserViewStates = hasAnyPermission(permissions, ['admin:users'], userRoles);
  const canManageRoles = hasAnyPermission(permissions, ['admin:users'], userRoles);
  const canImpersonate = userRoles.includes('super_admin');

  const { data, isLoading, error } = useGetCoachQuery({ coachId: coachId! }, { skip: !coachId });

  const [updateSettings, { isLoading: isUpdating }] = useUpdateCoachSettingsMutation();
  const [updateCoachStatus, { isLoading: isUpdatingStatus }] = useUpdateCoachStatusMutation();

  // Role management hooks
  const { data: roleData, isLoading: isLoadingRoles } = useGetAdminRolesQuery();
  const { data: personRolesData, isLoading: isLoadingPersonRoles } = useGetAdminPersonRolesQuery(
    { q: coachId },
    { skip: !coachId }
  );
  const [assignPersonRole, { isLoading: isAssigning }] = useAssignPersonRoleMutation();
  const [unassignPersonRole, { isLoading: isUnassigning }] = useUnassignPersonRoleMutation();
  const [bustRbacCache] = useBustRbacCacheMutation();

  // Impersonation hook
  const [startImpersonationApi, { isLoading: isImpersonating }] = useStartImpersonationMutation();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Role management state
  const [selectedRole, setSelectedRole] = useState<string>('');

  // Sorting state for members table - default to Sessions desc
  const [memberSortBy, setMemberSortBy] = useState<MemberSortBy>('sessions');
  const [memberSortDir, setMemberSortDir] = useState<SortDir>('desc');

  const coach = data?.coach;
  const members = data?.members ?? [];
  const stats = data?.stats;
  const skippedEmails = data?.skipped_emails ?? [];
  const googleOAuthStatus = coach?.google_oauth_status;
  const lifecycleStatus = coach?.lifecycle_status ?? 'unknown';
  const lifecycleActive = coach?.lifecycle_active === true;
  const googleProviders = (googleOAuthStatus?.providers || []).map(formatGoogleProviderLabel);
  const legacyGoogleProviders = (googleOAuthStatus?.legacy_providers || []).map(
    formatGoogleProviderLabel
  );

  // Sort members based on current sort state
  const sortedMembers = useMemo(() => {
    const sorted = [...members];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (memberSortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = (a.relationship_type ?? '').localeCompare(b.relationship_type ?? '');
          break;
        case 'sessions':
          comparison = a.session_count - b.session_count;
          break;
        case 'artifacts':
          comparison = a.artifact_count - b.artifact_count;
          break;
        case 'summaries':
          comparison = a.summary_sent_count - b.summary_sent_count;
          break;
        case 'last_activity': {
          const aDate = getLastActivity(a);
          const bDate = getLastActivity(b);
          if (!aDate && !bDate) comparison = 0;
          else if (!aDate) comparison = -1;
          else if (!bDate) comparison = 1;
          else comparison = aDate.getTime() - bDate.getTime();
          break;
        }
      }
      return memberSortDir === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [members, memberSortBy, memberSortDir]);

  const handleMemberSort = (column: MemberSortBy) => {
    if (memberSortBy === column) {
      // Toggle direction if same column
      setMemberSortDir(memberSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to desc for counts, asc for text
      setMemberSortBy(column);
      setMemberSortDir(['name', 'status'].includes(column) ? 'asc' : 'desc');
    }
  };

  const handleToggleAutoSummary = async () => {
    if (!coach) return;
    const newValue = !coach.auto_summary_enabled;
    const action = newValue ? 'Enable' : 'Disable';
    if (
      !window.confirm(
        `${action} auto-summary emails for ${coach.name}? Summary generation will continue.`
      )
    )
      return;

    try {
      await updateSettings({
        coachId: coach.id,
        settings: { auto_summary_enabled: newValue },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update settings:', err);
    }
  };

  const handleToggleReflectionEmail = async () => {
    if (!coach) return;
    try {
      await updateSettings({
        coachId: coach.id,
        settings: { auto_reflection_email_enabled: !(coach.auto_reflection_email_enabled ?? true) },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update reflection email setting:', err);
    }
  };

  const handleToggleDisabled = async () => {
    if (!coach) return;
    const isCurrentlyDisabled = !!coach.disabled_at;
    const newDisabledValue = !isCurrentlyDisabled;
    const action = newDisabledValue ? 'Disable' : 'Enable';
    const confirmMessage = newDisabledValue
      ? `Disable ${coach.name}?\n\nThey will no longer be able to log in or be assigned new clients. Their existing data will be preserved.\n\nThis can be reversed.`
      : `Enable ${coach.name}?\n\nThey will be able to log in and will appear in coach listings again.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await updateCoachStatus({
        coachId: coach.id,
        disabled: newDisabledValue,
      }).unwrap();
      setSnackbar({
        open: true,
        message: `Coach ${newDisabledValue ? 'disabled' : 'enabled'} successfully.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to update coach status:', err);
      setSnackbar({
        open: true,
        message: `Failed to ${action.toLowerCase()} coach. Please try again.`,
        severity: 'error',
      });
    }
  };

  const handleMemberClick = (memberId: string) => {
    navigate(`/admin/coaches/${coachId}/members/${memberId}`);
  };

  const handleBookSeriesClick = (memberId: string) => {
    navigate(`/admin/coaches/${coachId}/book-series?memberId=${memberId}`);
  };

  // Role management
  const allRoles = roleData?.roles ?? [];
  const coachPersonRoles = personRolesData?.items?.find((p) => p.person_id === coachId);
  const currentRoles = coachPersonRoles?.roles ?? [];
  const availableRoles: RbacRole[] = allRoles.filter(
    (r) => !currentRoles.some((cr) => cr.role_name === r.name)
  );

  const handleAssignRole = async () => {
    if (!selectedRole || !coachId || !coach) return;
    try {
      await assignPersonRole({
        personId: coachId,
        roleName: selectedRole,
        orgId: coach.org_id || undefined,
      }).unwrap();
      // Best-effort cache bust - backend already busts cache, this is a secondary safety net
      bustRbacCache({ personId: coachId, orgId: coach.org_id || undefined }).catch((err) =>
        console.warn('Cache bust failed (non-critical):', err)
      );
      setSelectedRole('');
      setSnackbar({
        open: true,
        message: `Role "${selectedRole}" assigned successfully.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to assign role:', err);
      setSnackbar({
        open: true,
        message: 'Failed to assign role. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleUnassignRole = async (roleName: string, orgId?: string | null) => {
    if (!coachId || !coach) return;
    if (!window.confirm(`Remove role "${roleName}" from ${coach.name}?`)) return;
    try {
      await unassignPersonRole({
        personId: coachId,
        roleName,
        orgId: orgId || undefined,
      }).unwrap();
      // Best-effort cache bust - backend already busts cache, this is a secondary safety net
      bustRbacCache({ personId: coachId, orgId: coach.org_id || undefined }).catch((err) =>
        console.warn('Cache bust failed (non-critical):', err)
      );
      setSnackbar({
        open: true,
        message: `Role "${roleName}" removed successfully.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to remove role:', err);
      setSnackbar({
        open: true,
        message:
          (err as any)?.data?.error === 'role_managed_by_sync'
            ? (err as any).data.error_description
            : 'Failed to remove role. Please try again.',
        severity: 'error',
      });
    }
  };

  // Impersonation
  const handleImpersonate = async () => {
    if (!coach || !currentToken || !currentUser) return;
    if (
      !window.confirm(
        `Impersonate ${coach.name}? You will be redirected to the home page as this user.`
      )
    )
      return;

    try {
      const result = await startImpersonationApi({ target_person_id: coach.id }).unwrap();
      startAdminImpersonationSession({
        dispatch,
        navigate,
        currentToken,
        currentUser,
        result,
      });
    } catch (err) {
      console.error('Failed to start impersonation:', err);
      const errorMessage =
        (err as any)?.data?.error_description ||
        (err as any)?.message ||
        'Failed to start impersonation. Please try again.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const isRoleBusy = isAssigning || isUnassigning || isLoadingRoles || isLoadingPersonRoles;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !coach) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
        <Alert severity="error">
          Failed to load coach details. {(error as any)?.data?.error_description || ''}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/coaches')}
          sx={{ mt: 2 }}
        >
          Back to Coaches
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <CoachProfileNavigation coachId={coach.id} activeTab="overview" />

      {/* Disabled Banner */}
      {coach.disabled_at && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This coach is <strong>disabled</strong> as of{' '}
          {format(new Date(coach.disabled_at), 'MMM d, yyyy h:mm a')}. They cannot log in and are
          hidden from coach listings.
        </Alert>
      )}

      {/* Coach Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5">
              {coach.name}
              {coach.disabled_at && (
                <Chip
                  label="Disabled"
                  color="warning"
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {coach.email}
            </Typography>
            {coach.org_name && (
              <Typography variant="body2" color="text.secondary">
                Organization: {coach.org_name}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`Lifecycle ${lifecycleStatus}`}
              color={
                lifecycleActive ? 'success' : lifecycleStatus === 'inactive' ? 'warning' : 'default'
              }
              variant="outlined"
            />
            {coach.drive_connected ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Drive Connected"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<CloudOffIcon />}
                label="Drive Not Connected"
                color="default"
                variant="outlined"
              />
            )}
            {canImpersonate && (
              <Tooltip title="View as this coach">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isImpersonating ? <CircularProgress size={16} /> : <VisibilityIcon />}
                  onClick={handleImpersonate}
                  disabled={isImpersonating}
                >
                  Impersonate
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {coach.drive_connected_at && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Drive connected: {format(new Date(coach.drive_connected_at), 'MMM d, yyyy')}
          </Typography>
        )}
        {coach.last_active_at && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Last active: {formatDistanceToNow(new Date(coach.last_active_at), { addSuffix: true })}
          </Typography>
        )}
      </Paper>

      {googleOAuthStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Google OAuth
            </Typography>
            {googleOAuthStatus.migration_required ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Google reconnect required for {legacyGoogleProviders.join(', ')}. This coach still
                has legacy Google OAuth credentials that need to be migrated.
              </Alert>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                size="small"
                color={googleOAuthStatus.connected ? 'success' : 'default'}
                label={
                  googleOAuthStatus.connected
                    ? `Google ${googleOAuthStatus.token_status || 'active'}`
                    : `Google ${googleOAuthStatus.token_status || 'disconnected'}`
                }
              />
              {googleOAuthStatus.migration_required ? (
                <Chip size="small" color="warning" label="Migration Required" />
              ) : (
                <Chip size="small" color="success" variant="outlined" label="Primary Client Only" />
              )}
              {googleProviders.map((provider) => (
                <Chip key={provider} size="small" variant="outlined" label={provider} />
              ))}
            </Box>
            {googleOAuthStatus.email ? (
              <Typography variant="body2" color="text.secondary">
                Connected account: {googleOAuthStatus.email}
              </Typography>
            ) : null}
            {googleOAuthStatus.last_synced_at ? (
              <Typography variant="body2" color="text.secondary">
                Last synced:{' '}
                {format(new Date(googleOAuthStatus.last_synced_at), 'MMM d, yyyy h:mm a')}
              </Typography>
            ) : null}
            {googleOAuthStatus.updated_at ? (
              <Typography variant="body2" color="text.secondary">
                Status updated:{' '}
                {formatDistanceToNow(new Date(googleOAuthStatus.updated_at), { addSuffix: true })}
              </Typography>
            ) : null}
            {googleOAuthStatus.last_error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Last Google error: {googleOAuthStatus.last_error}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Settings
          </Typography>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box>
              <Typography variant="body1">Auto-Summary emails</Typography>
              <Typography variant="body2" color="text.secondary">
                {coach.auto_summary_enabled
                  ? 'Send an email when an automatic coaching summary is ready'
                  : 'Automatic coaching-summary emails are disabled. Summary generation continues.'}
              </Typography>
              {coach.auto_summary_enabled_at && (
                <Typography variant="caption" color="text.secondary">
                  Enabled: {format(new Date(coach.auto_summary_enabled_at), 'MMM d, yyyy h:mm a')}
                </Typography>
              )}
            </Box>
            <Switch
              checked={coach.auto_summary_enabled}
              onChange={handleToggleAutoSummary}
              disabled={isUpdating}
            />
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box>
              <Typography variant="body1">Reflection emails</Typography>
              <Typography variant="body2" color="text.secondary">
                Send an email when an automatic Coaching Reflection or Chem Call Reflection is
                ready. Turning this off does not stop reflection generation.
              </Typography>
            </Box>
            <Switch
              checked={coach.auto_reflection_email_enabled ?? true}
              onChange={handleToggleReflectionEmail}
              disabled={isUpdating}
              inputProps={{ 'aria-label': 'Reflection emails' }}
            />
          </Box>
          {/* Account Status Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 3,
              pt: 3,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="body1">Account Status</Typography>
              <Typography variant="body2" color="text.secondary">
                {coach.disabled_at
                  ? 'This coach is disabled and cannot log in or be assigned new clients.'
                  : 'This coach is active and can log in normally.'}
              </Typography>
              {coach.disabled_at && (
                <Typography variant="caption" color="text.secondary">
                  Disabled: {format(new Date(coach.disabled_at), 'MMM d, yyyy h:mm a')}
                </Typography>
              )}
            </Box>
            <Button
              variant={coach.disabled_at ? 'contained' : 'outlined'}
              color={coach.disabled_at ? 'success' : 'error'}
              onClick={handleToggleDisabled}
              disabled={isUpdatingStatus}
              startIcon={isUpdatingStatus ? <CircularProgress size={16} /> : undefined}
            >
              {coach.disabled_at ? 'Enable Coach' : 'Disable Coach'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Roles Card */}
      {canManageRoles && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Roles
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                {currentRoles.length === 0 ? (
                  <Chip size="small" label="No roles" color="default" />
                ) : (
                  currentRoles.map((role) => (
                    <Chip
                      key={`${role.role_name}-${role.org_id || 'global'}`}
                      size="small"
                      label={role.role_name}
                      onDelete={
                        isRoleBusy
                          ? undefined
                          : () => handleUnassignRole(role.role_name, role.org_id)
                      }
                      deleteIcon={<DeleteIcon />}
                      disabled={isRoleBusy}
                    />
                  ))
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Select
                  size="small"
                  displayEmpty
                  disabled={isRoleBusy || availableRoles.length === 0}
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  renderValue={(value) => value || 'Select a role'}
                  sx={{ minWidth: 160 }}
                >
                  {availableRoles.length === 0 ? (
                    <MenuItem disabled value="">
                      No available roles
                    </MenuItem>
                  ) : (
                    availableRoles.map((role) => (
                      <MenuItem key={role.name} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <Button
                  size="small"
                  variant="contained"
                  disabled={isRoleBusy || !selectedRole}
                  onClick={handleAssignRole}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4">{stats.total_members}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Members ({stats.active_members} active)
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4">{stats.total_sessions}</Typography>
              <Typography variant="body2" color="text.secondary">
                Sessions
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4">{stats.total_artifacts}</Typography>
              <Typography variant="body2" color="text.secondary">
                Artifacts
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Members Table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Members
      </Typography>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={memberSortBy === 'name'}
                    direction={memberSortBy === 'name' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('name')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={memberSortBy === 'status'}
                    direction={memberSortBy === 'status' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortBy === 'sessions'}
                    direction={memberSortBy === 'sessions' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('sessions')}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortBy === 'artifacts'}
                    direction={memberSortBy === 'artifacts' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('artifacts')}
                  >
                    Artifacts
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortBy === 'summaries'}
                    direction={memberSortBy === 'summaries' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('summaries')}
                  >
                    Summaries Sent
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={memberSortBy === 'last_activity'}
                    direction={memberSortBy === 'last_activity' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('last_activity')}
                  >
                    Last Activity
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMembers.map((member) => (
                <TableRow
                  key={member.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleMemberClick(member.id)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{member.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {member.relationship_type && (
                      <Chip
                        label={member.relationship_type.replace(/_/g, ' ')}
                        size="small"
                        color={member.relationship_type === 'active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{member.session_count}</TableCell>
                  <TableCell align="right">{member.artifact_count}</TableCell>
                  <TableCell align="right">{member.summary_sent_count}</TableCell>
                  <TableCell>{formatLastActivity(member)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleBookSeriesClick(member.id);
                      }}
                    >
                      Book Series
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {members.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No members found.</Typography>
          </Box>
        )}
      </Paper>

      {/* Skipped Emails Section */}
      {skippedEmails.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Skipped Email Attempts ({skippedEmails.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These transcripts could not be attributed to a specific coachee
          </Typography>
          <Paper>
            <List>
              {skippedEmails.map((email, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={`Skipped: ${email.skip_reason?.replace(/_/g, ' ') ?? 'unknown reason'}`}
                    secondary={
                      <>
                        {email.transcript_date && (
                          <span>
                            Transcript date: {new Date(email.transcript_date).toLocaleDateString()}
                          </span>
                        )}
                        {email.transcript_date && ' • '}
                        <span>Attempted: {formatDistanceToNow(new Date(email.sent_at))} ago</span>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Dismissable views
          </Typography>
          {canManageUserViewStates ? (
            <DismissableViewsPanel personId={coach.id} />
          ) : (
            <Alert severity="info">
              You need the <code>admin:users</code> permission to inspect and manage dismissable
              view state.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
